import { useEffect, useState, useRef } from 'react';

interface PullToRefreshOptions {
  /**
   * Maximum distance from top of viewport where touch must start (in pixels)
   * @default 100
   */
  startZoneHeight?: number;
  /**
   * Fraction of screen height the user must swipe to trigger refresh
   * @default 0.33 (1/3 of screen)
   */
  thresholdFraction?: number;
  /**
   * Maximum pull distance in pixels (for visual indicator)
   * @default 150
   */
  maxPullDistance?: number;
  /**
   * Callback function when refresh is triggered
   */
  onRefresh?: () => void | Promise<void>;
}

interface PullToRefreshState {
  isPulling: boolean;
  pullDistance: number;
  isRefreshing: boolean;
  /** Progress toward threshold (0-1) */
  progress: number;
}

/**
 * Hook to enable pull-to-refresh functionality
 * Useful for PWAs to allow users to hard refresh after deployments
 *
 * Requirements:
 * - Touch must START within the top zone of the screen (startZoneHeight)
 * - User must swipe down at least 1/3 of the screen height
 */
export function usePullToRefresh(options: PullToRefreshOptions = {}) {
  const {
    startZoneHeight = 100,
    thresholdFraction = 0.33,
    maxPullDistance = 150,
    onRefresh,
  } = options;

  const [state, setState] = useState<PullToRefreshState>({
    isPulling: false,
    pullDistance: 0,
    isRefreshing: false,
    progress: 0,
  });

  const touchStartY = useRef<number>(0);
  const touchCurrentY = useRef<number>(0);
  const isValidPullStart = useRef<boolean>(false);

  // Use refs to track state values for use in event handlers
  // This prevents the effect from re-running on every state change
  const stateRef = useRef(state);
  const onRefreshRef = useRef(onRefresh);

  // Keep refs in sync with state/props
  stateRef.current = state;
  onRefreshRef.current = onRefresh;

  useEffect(() => {
    // Calculate threshold based on screen height
    const getThreshold = () => Math.round(window.innerHeight * thresholdFraction);

    let supportsPassive = false;
    try {
      const opts = Object.defineProperty({}, 'passive', {
        get() {
          supportsPassive = true;
        },
      });
      window.addEventListener('testPassive', null as any, opts);
      window.removeEventListener('testPassive', null as any, opts);
    } catch (e) {
      // Passive not supported
    }

    const handleTouchStart = (e: TouchEvent) => {
      const touchY = e.touches[0].clientY;

      // Only allow pull-to-refresh if:
      // 1. Page is scrolled to the top
      // 2. Touch starts within the top zone of the viewport
      if (window.scrollY === 0 && touchY <= startZoneHeight) {
        touchStartY.current = touchY;
        isValidPullStart.current = true;
      } else {
        isValidPullStart.current = false;
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      // Only process if this is a valid pull-to-refresh gesture
      // Use ref for isRefreshing to avoid stale closure
      if (!isValidPullStart.current || stateRef.current.isRefreshing) return;

      touchCurrentY.current = e.touches[0].clientY;
      const pullDistance = Math.max(0, touchCurrentY.current - touchStartY.current);

      // Only trigger pull if pulling down from valid start position
      if (pullDistance > 0) {
        // Prevent default scrolling behavior once we're clearly pulling
        if (pullDistance > 20) {
          e.preventDefault();
        }

        const threshold = getThreshold();
        const clampedDistance = Math.min(pullDistance, maxPullDistance);
        const progress = Math.min(pullDistance / threshold, 1);

        setState({
          isPulling: clampedDistance > 20,
          pullDistance: clampedDistance,
          isRefreshing: false,
          progress,
        });
      }
    };

    const handleTouchEnd = async () => {
      // Only trigger refresh if this was a valid pull gesture
      if (!isValidPullStart.current) {
        return;
      }

      const threshold = getThreshold();
      const pullDistance = touchCurrentY.current - touchStartY.current;

      // Use ref to get current state and avoid stale closure issues
      const currentIsRefreshing = stateRef.current.isRefreshing;

      if (pullDistance >= threshold && !currentIsRefreshing) {
        setState({
          isPulling: false,
          pullDistance: 0,
          isRefreshing: true,
          progress: 1,
        });

        // Execute custom refresh callback if provided
        if (onRefreshRef.current) {
          await onRefreshRef.current();
        }

        // Hard reload to clear all caches
        window.location.reload();
      } else {
        setState({
          isPulling: false,
          pullDistance: 0,
          isRefreshing: false,
          progress: 0,
        });
      }

      // Reset valid pull state
      isValidPullStart.current = false;
    };

    const passiveOption = supportsPassive ? { passive: false } : false;

    document.addEventListener('touchstart', handleTouchStart, passiveOption);
    document.addEventListener('touchmove', handleTouchMove, passiveOption);
    document.addEventListener('touchend', handleTouchEnd);

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  // Only include stable values in dependencies - refs handle the changing state
  }, [startZoneHeight, thresholdFraction, maxPullDistance]);

  return state;
}
